import React, { useState, useEffect } from "react";
import { settingsAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Building2,
  CreditCard,
  FileText,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const canEdit = isAdmin();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage company and invoice settings</p>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        )}
      </div>

      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company">
            <Building2 className="w-4 h-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="bank">
            <CreditCard className="w-4 h-4 mr-2" />
            Bank Details
          </TabsTrigger>
          <TabsTrigger value="invoice">
            <FileText className="w-4 h-4 mr-2" />
            Invoice Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Your business details that appear on invoices</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Company Name</Label>
                  <Input
                    value={settings?.company_name || ""}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input
                    value={settings?.gstin || ""}
                    onChange={(e) => handleChange("gstin", e.target.value)}
                    disabled={!canEdit}
                    className="font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>PAN</Label>
                  <Input
                    value={settings?.pan || ""}
                    onChange={(e) => handleChange("pan", e.target.value)}
                    disabled={!canEdit}
                    className="font-mono"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={settings?.address_line1 || ""}
                    onChange={(e) => handleChange("address_line1", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={settings?.address_line2 || ""}
                    onChange={(e) => handleChange("address_line2", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={settings?.city || ""}
                    onChange={(e) => handleChange("city", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={settings?.state || ""}
                    onChange={(e) => handleChange("state", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={settings?.pincode || ""}
                    onChange={(e) => handleChange("pincode", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>State Code</Label>
                  <Input
                    value={settings?.state_code || ""}
                    onChange={(e) => handleChange("state_code", e.target.value)}
                    disabled={!canEdit}
                    maxLength={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={settings?.phone || ""}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={settings?.email || ""}
                    onChange={(e) => handleChange("email", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bank" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Bank Account Details</CardTitle>
              <CardDescription>Payment details shown on invoices</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Bank Name & Branch</Label>
                  <Input
                    value={settings?.bank_name || ""}
                    onChange={(e) => handleChange("bank_name", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={settings?.bank_account || ""}
                    onChange={(e) => handleChange("bank_account", e.target.value)}
                    disabled={!canEdit}
                    className="font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={settings?.bank_ifsc || ""}
                    onChange={(e) => handleChange("bank_ifsc", e.target.value)}
                    disabled={!canEdit}
                    className="font-mono"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label>UPI ID</Label>
                  <Input
                    value={settings?.bank_upi || ""}
                    onChange={(e) => handleChange("bank_upi", e.target.value)}
                    disabled={!canEdit}
                    className="font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoice" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Invoice Numbering</CardTitle>
              <CardDescription>Configure invoice number prefixes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Prefix</Label>
                  <Input
                    value={settings?.invoice_prefix || ""}
                    onChange={(e) => handleChange("invoice_prefix", e.target.value)}
                    disabled={!canEdit}
                    placeholder="BC"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Proforma Prefix</Label>
                  <Input
                    value={settings?.proforma_prefix || ""}
                    onChange={(e) => handleChange("proforma_prefix", e.target.value)}
                    disabled={!canEdit}
                    placeholder="PI"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Receipt Prefix</Label>
                  <Input
                    value={settings?.payment_prefix || ""}
                    onChange={(e) => handleChange("payment_prefix", e.target.value)}
                    disabled={!canEdit}
                    placeholder="REC"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">
                  <strong>Preview:</strong> {settings?.invoice_prefix || "BC"}/{new Date().getFullYear()}/00001
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Invoice Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Round Off Total</Label>
                  <p className="text-sm text-slate-500">Automatically round invoice totals to nearest rupee</p>
                </div>
                <Switch
                  checked={settings?.round_off_enabled !== false}
                  onCheckedChange={(checked) => handleChange("round_off_enabled", checked)}
                  disabled={!canEdit}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Default Terms & Conditions</CardTitle>
              <CardDescription>These will appear on all invoices by default</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings?.default_terms || ""}
                onChange={(e) => handleChange("default_terms", e.target.value)}
                disabled={!canEdit}
                rows={5}
                placeholder="Enter your payment terms and conditions..."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
